import { useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import gravatar from 'gravatar';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Input,
} from '@chakra-ui/react';

import {
  OrganizationTier,
  useDeleteOrganizationMutation,
  useOrgSettingPageQuery,
  useSetOrgLogoUrlMutation,
  useSetOrgNameMutation,
} from 'src/generated/graphql';
import { LoadingScreen } from 'src/LoadingScreen';
import { routes } from 'src/routes';

const getTierDisplayName = (tier: OrganizationTier): string => {
  switch (tier) {
    case OrganizationTier.Pro:
      return 'Pro';
    case OrganizationTier.Free:
      return 'Free';
    case OrganizationTier.LegacyFree:
      return 'Legacy Free';
    default:
      return tier;
  }
};

export const OrgGeneralSettings = ({ orgSlug }: { orgSlug: string }) => {
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgLogoUrl, setNewOrgLogoUrl] = useState('');

  const [isDeleteOrgOpen, setIsDeleteOrgOpen] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string>();
  const cancelDeleteOrg = useRef<any>();

  const [renameOrganization] = useSetOrgNameMutation({
    variables: {
      newName: newOrgName,
      organizationSlug: orgSlug,
    },
  });

  const [setOrgLogoUrl] = useSetOrgLogoUrlMutation({
    variables: {
      newLogoUrl: newOrgLogoUrl,
      organizationSlug: orgSlug,
    },
  });

  const [deleteOrganization] = useDeleteOrganizationMutation({
    variables: {
      organizationSlug: orgSlug,
    },
  });

  const { data, loading } = useOrgSettingPageQuery({
    variables: {
      organizationSlug: orgSlug,
    },
  });

  const memberships = data?.currentUser?.memberships;

  if (loading) {
    return <LoadingScreen />;
  }

  if (!memberships) {
    return <div>No data</div>;
  }

  if (redirectPath) {
    return <Redirect to={redirectPath} push />;
  }

  const organizationId = data?.organization?.id;

  if (!organizationId) {
    return <div>No data</div>;
  }

  const isCurrentUserAdmin =
    data.currentUser?.memberships.find(
      ({ organizationId }) => organizationId === data?.organization?.id,
    )?.role === 'ADMIN';

  return (
    <div>
      {memberships
        .filter((membership) => membership.organization.slug === orgSlug)
        .map((membership) => {
          if (!membership) return null;

          return (
            <div key={membership.id} className="flex flex-col">
              <div className="flex flex-col border rounded-md mb-12 flex-shrink-0">
                <h2 className="font-bold ml-4 mt-6">Current Plan</h2>
                <div className="my-6 mx-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    {data?.organization?.tier
                      ? getTierDisplayName(data.organization.tier)
                      : 'Unknown'}
                  </span>
                </div>
              </div>

              <div className="flex flex-col border rounded-md mb-12 flex-shrink-0">
                <h2 className="font-bold ml-4 mt-6">Team Name</h2>
                <div className="my-6 mx-4">
                  <Input
                    id="newOrgName"
                    size="sm"
                    disabled={!isCurrentUserAdmin}
                    defaultValue={membership.organization.name?.toString()}
                    onChange={(e) => {
                      setNewOrgName(e.target.value);
                    }}
                  />
                </div>

                <Button
                  className="ml-auto mb-4 mr-4"
                  type="submit"
                  colorScheme="blue"
                  size="sm"
                  transition="none"
                  disabled={
                    newOrgName === '' ||
                    newOrgName === membership.organization.name ||
                    !isCurrentUserAdmin
                  }
                  onClick={() => {
                    renameOrganization({
                      variables: {
                        newName: newOrgName,
                        organizationSlug: orgSlug,
                      },
                    });
                  }}
                >
                  Save
                </Button>
              </div>

              <div className="flex flex-col border rounded-md mb-12 flex-shrink-0">
                <h2 className="font-bold ml-4 mt-6">Team Avatar</h2>
                <div className="my-6 mx-4">
                  <Input
                    id="newOrgLogoUrl"
                    size="sm"
                    disabled={!isCurrentUserAdmin}
                    defaultValue={membership.organization.logoUrl?.toString()}
                    onChange={(e) => setNewOrgLogoUrl(e.target.value)}
                  />
                </div>

                <Button
                  className="ml-auto mb-4 mr-4"
                  type="submit"
                  colorScheme="blue"
                  size="sm"
                  transition="none"
                  disabled={
                    newOrgLogoUrl === '' ||
                    newOrgLogoUrl === membership.organization.logoUrl ||
                    !isCurrentUserAdmin
                  }
                  onClick={() => {
                    setOrgLogoUrl({
                      variables: {
                        newLogoUrl: newOrgLogoUrl,
                        organizationSlug: orgSlug,
                      },
                    });
                  }}
                >
                  Save
                </Button>
              </div>

              <div className="flex flex-col border border-red-400 rounded-md mb-12 flex-shrink-0">
                <h2 className="font-bold ml-4 mt-6">Delete Team</h2>
                <span className="flex items-center my-4 mx-4">
                  <img
                    src={
                      membership?.organization?.logoUrl ??
                      gravatar.url(`${membership.organization.slug}@mailcrunch.io`, { s: '120', d: 'identicon' })
                    }
                    alt=""
                    className="w-12 h-12 rounded-full overflow-hidden bg-gray-300 mr-4"
                  />
                  <span className="font-medium">
                    {membership.organization.slug}
                  </span>
                </span>

                <Button
                  className="ml-auto mb-4 mr-4"
                  type="submit"
                  colorScheme="red"
                  size="sm"
                  transition="none"
                  disabled={!isCurrentUserAdmin}
                  onClick={() => {
                    setIsDeleteOrgOpen(true);
                  }}
                >
                  Delete
                </Button>
                <AlertDialog
                  isOpen={isDeleteOrgOpen}
                  leastDestructiveRef={cancelDeleteOrg}
                  onClose={() => {
                    setIsDeleteOrgOpen(false);
                  }}
                >
                  <AlertDialogOverlay />
                  <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                      Delete Team
                    </AlertDialogHeader>

                    <AlertDialogBody>
                      Are you sure you want to delete this team?
                    </AlertDialogBody>

                    <AlertDialogFooter>
                      <Button
                        ref={cancelDeleteOrg}
                        size={'sm'}
                        onClick={() => {
                          setIsDeleteOrgOpen(false);
                        }}
                        mr={3}
                      >
                        Cancel
                      </Button>
                      <Button
                        colorScheme="red"
                        size={'sm'}
                        onClick={async (e) => {
                          e.preventDefault();

                          await deleteOrganization({
                            variables: {
                              organizationSlug: membership.organization.slug,
                            },
                          });

                          setIsDeleteOrgOpen(false);

                          if (!data?.currentUser?.memberships) return null;

                          const anotherOrgSlug =
                            data?.currentUser?.memberships?.[0]?.organization
                              ?.slug === orgSlug
                              ? data?.currentUser?.memberships?.[1]
                                  ?.organization?.slug
                              : data?.currentUser?.memberships?.[0]
                                  ?.organization?.slug;

                          setRedirectPath(
                            routes.projects.getPath({
                              orgSlug: anotherOrgSlug,
                            }),
                          );
                        }}
                      >
                        Delete
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          );
        })}
    </div>
  );
};
